// src/App.tsx
import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { db } from "./firebaseConfig";
import { ref, push, onChildAdded } from "firebase/database";
import { useRole } from "./RoleContext";
import {
  addUser,
  subscribeUsers,
  type AppUser,
  setUserActive,
  deleteUser,
  subscribeLogs,
  type LogEntry,
  addLog,
  getUserById,
  updateUserPassword,
} from "./adminApi";

/* ===================== Constantes session/local ===================== */
const USER_FIRST_KEY = "cpas_user_first";
const USER_LAST_KEY = "cpas_user_last";
const USER_EMAIL_KEY = "cpas_user_email";
const USER_ID_KEY = "cpas_user_id";
const LAST_SEEN_ALERT_TS = "cpas_last_seen_ts";

/* ===================== Types ===================== */
type AlertType = "collegue" | "police";
interface AlertPayload {
  id: string;
  agent: string;
  time: string;
  timestamp: number;
  type: AlertType;
}

/* ===================== Helpers ===================== */
function safeSortUsers(list: AppUser[]): AppUser[] {
  return (list || [])
    .filter(Boolean)
    .sort((a, b) => {
      const aLast = (a?.lastName || "").toString();
      const bLast = (b?.lastName || "").toString();
      const lastCmp = aLast.localeCompare(bLast, "fr", { sensitivity: "base" });
      if (lastCmp !== 0) return lastCmp;
      const aFirst = (a?.firstName || "").toString();
      const bFirst = (b?.firstName || "").toString();
      return aFirst.localeCompare(bFirst, "fr", { sensitivity: "base" });
    });
}

async function logSafely(entry: Parameters<typeof addLog>[0]) {
  try {
    await addLog(entry);
  } catch (e) {
    console.warn("[logs] addLog a échoué :", e);
  }
}

/* ===================== UI: Aide & Confirm ===================== */
function HelpMenu() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Afficher/masquer l’aide"
        aria-expanded={open}
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 1000,
          width: 40,
          height: 40,
          borderRadius: 8,
          border: "none",
          background: "#1f2f58",
          color: "white",
          fontSize: 20,
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      >
        ☰
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: 64,
            left: 16,
            zIndex: 900,
            width: 300,
            background: "white",
            borderRadius: 12,
            boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
            border: "1px solid #e5e7eb",
            padding: 16,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            🧯 Services de secours nationaux
          </div>
          <ul style={{ marginTop: 0, marginBottom: 12 }}>
            <li>112 – Urgences (ambulance, pompiers, police)</li>
          </ul>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>👮‍♀️ Police locale</div>
          <ul style={{ marginTop: 0, marginBottom: 12 }}>
            <li>101 – Police</li>
          </ul>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>🧠 Assistance</div>
          <ul style={{ marginTop: 0, marginBottom: 12 }}>
            <li>107 – Télé-Accueil (24h/24)</li>
          </ul>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>🗂️ Contacts CPAS</div>
          <ul style={{ marginTop: 0 }}>
            <li>Responsable sécurité : [Nom + numéro]</li>
          </ul>
        </div>
      )}
    </>
  );
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 96vw)",
          background: "white",
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          padding: 20,
        }}
      >
        <h3 style={{ margin: "0 0 8px", color: "#111827" }}>{title}</h3>
        <p style={{ margin: "0 0 16px", color: "#374151" }}>{message}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              background: "#e5e7eb",
              color: "#111827",
              border: "none",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: "#dc2626",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Modal: Changer mot de passe ===================== */
function ChangePasswordModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(
    null
  );

  if (!open) return null;

  const userId = sessionStorage.getItem(USER_ID_KEY);
  const email = sessionStorage.getItem(USER_EMAIL_KEY) || undefined;
  const first = sessionStorage.getItem(USER_FIRST_KEY) || undefined;
  const last = sessionStorage.getItem(USER_LAST_KEY) || undefined;

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setMsg(null);

    if (!userId) {
      setMsg({
        type: "err",
        text: "Cette fonction n'est pas disponible pour l'accès 'secours'.",
      });
      return;
    }
    if (!currentPwd.trim() || !newPwd.trim() || !confirmPwd.trim()) {
      setMsg({ type: "err", text: "Remplis tous les champs." });
      return;
    }
    if (newPwd !== confirmPwd) {
      setMsg({
        type: "err",
        text: "Le nouveau mot de passe et sa confirmation ne correspondent pas.",
      });
      return;
    }
    if (newPwd.length < 8) {
      setMsg({
        type: "err",
        text: "Le nouveau mot de passe doit contenir au moins 8 caractères.",
      });
      return;
    }
    if (newPwd === currentPwd) {
      setMsg({
        type: "err",
        text: "Le nouveau mot de passe doit être différent de l’actuel.",
      });
      return;
    }

    setBusy(true);
    try {
      const user = await getUserById(userId);
      if (!user) {
        setMsg({ type: "err", text: "Compte introuvable." });
        setBusy(false);
        return;
      }
      if (user.password !== currentPwd) {
        setMsg({ type: "err", text: "Mot de passe actuel incorrect." });
        setBusy(false);
        return;
      }
      await updateUserPassword(userId, newPwd);
      await addLog({
        type: "password_change",
        email,
        firstName: first,
        lastName: last,
        details: "Changement via Mon compte",
      });
      setMsg({ type: "ok", text: "Mot de passe mis à jour ✔" });
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setTimeout(onClose, 800);
    } catch {
      setMsg({
        type: "err",
        text: "Échec de la mise à jour (réseau/permissions ?).",
      });
    } finally {
      setBusy(false);
    }
  };

  const field: React.CSSProperties = {
    width: "100%",
    height: 40,
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    padding: "0 12px",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2200,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 96vw)",
          background: "white",
          borderRadius: 12,
          boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
          padding: 20,
        }}
      >
        <h3 style={{ margin: "0 0 10px" }}>Changer mon mot de passe</h3>
        <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={{ fontSize: 13 }}>Mot de passe actuel</label>
            <input
              type={show ? "text" : "password"}
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              style={field}
            />
          </div>
          <div>
            <label style={{ fontSize: 13 }}>Nouveau mot de passe</label>
            <input
              type={show ? "text" : "password"}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              style={field}
            />
          </div>
          <div>
            <label style={{ fontSize: 13 }}>
              Confirmer le nouveau mot de passe
            </label>
            <input
              type={show ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              style={field}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              id="showpwd"
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
            />
            <label htmlFor="showpwd" style={{ userSelect: "none" }}>
              Afficher les mots de passe
            </label>
          </div>

          {msg && (
            <div
              style={{
                background: msg.type === "ok" ? "#ecfdf5" : "#fef2f2",
                color: msg.type === "ok" ? "#065f46" : "#b91c1c",
                border: `1px solid ${
                  msg.type === "ok" ? "#a7f3d0" : "#fecaca"
                }`,
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 13,
              }}
            >
              {msg.text}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "#e5e7eb",
                border: "none",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={busy}
              style={{
                background: "#234993",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              {busy ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===================== Page Admin ===================== */
function AdminPage({ onBack }: { onBack: () => void }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"user" | "admin">("user");
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<AppUser | null>(null);

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filterType] = useState<string>("all");
  const [search, setSearch] = useState<string>("");

  const currentEmail =
    (localStorage.getItem("cpas_alert_last_email") || "").toLowerCase();

  useEffect(() => {
    const unsubUsers = subscribeUsers((u) => setUsers(safeSortUsers(u)));
    const unsubLogs = subscribeLogs((entries) => setLogs(entries));
    return () => {
      unsubUsers();
      unsubLogs();
    };
  }, []);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setRole("user");
  };

  const handleAdd = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
      alert("Complète prénom, nom, e-mail et mot de passe provisoire.");
      return;
    }
    setSaving(true);
    try {
      await addUser({ firstName, lastName, email, password, role });
      resetForm();
      alert("Utilisateur créé ✔");
    } catch {
      alert("Échec de la création (permissions réseau/DB ?)");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u: AppUser) => {
    setBusyId(u.id);
    try {
      await setUserActive(u.id, !(u.active !== false));
    } catch {
      alert("Impossible de modifier l’état (vérifie les règles Firebase).");
    } finally {
      setBusyId(null);
    }
  };

  const askDelete = (u: AppUser) => {
    if (u.email.toLowerCase() === currentEmail) {
      alert("Impossible de supprimer l’accès en cours.");
      return;
    }
    setToDelete(u);
    setConfirmOpen(true);
  };
  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusyId(toDelete.id);
    setConfirmOpen(false);
    try {
      await deleteUser(toDelete.id);
    } catch {
      alert("Suppression impossible (permissions réseau/DB ?).");
    } finally {
      setBusyId(null);
      setToDelete(null);
    }
  };

  const filteredLogs = logs.filter((l) => {
    const typeOk = filterType === "all" ? true : l.type === (filterType as any);
    const s = search.trim().toLowerCase();
    if (!s) return typeOk;
    const who = `${l.firstName || ""} ${l.lastName || ""} ${l.email || ""} ${
      l.agentName || ""
    }`.toLowerCase();
    return typeOk && who.includes(s);
  });

  return (
    <div
      style={{
        width: "min(1100px, 96vw)",
        background: "white",
        borderRadius: 12,
        padding: 24,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "#e5e7eb",
            border: "none",
            borderRadius: 8,
            padding: "8px 10px",
            cursor: "pointer",
          }}
        >
          ← Retour
        </button>
        <h2 style={{ margin: 0, color: "#234993" }}>Espace Administrateur</h2>
      </div>

      {/* Création d'accès */}
      <h3 style={{ marginTop: 8 }}>Créer un accès</h3>
      <form
        onSubmit={handleAdd}
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
      >
        <div>
          <label style={{ fontSize: 13 }}>Prénom</label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
            }}
          />
        </div>
        <div>
          <label style={{ fontSize: 13 }}>Nom</label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
            }}
          />
        </div>
        <div>
          <label style={{ fontSize: 13 }}>Identifiant (e-mail)</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
            }}
          />
        </div>
        <div>
          <label style={{ fontSize: 13 }}>Mot de passe provisoire</label>
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
            }}
          />
        </div>
        <div>
          <label style={{ fontSize: 13 }}>Rôle</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as "user" | "admin")}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
            }}
          >
            <option value="user">Utilisateur</option>
            <option value="admin">Administrateur</option>
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "end" }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: "#234993",
              color: "white",
              border: "none",
              padding: "10px 16px",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            {saving ? "Création..." : "Ajouter"}
          </button>
        </div>
      </form>

      {/* Liste utilisateurs */}
      <h3 style={{ marginTop: 20 }}>Utilisateurs existants</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Nom
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                E-mail
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Rôle
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Actif
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Créé le
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.email.toLowerCase() === currentEmail;
              const active = u.active !== false;
              return (
                <tr key={u.id}>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {(u.lastName || "—")} {(u.firstName || "")}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {u.email || "—"}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {u.role === "admin" ? "Admin" : "Utilisateur"}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {active ? "Oui" : "Non"}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {u.createdAt ? new Date(u.createdAt).toLocaleString() : "—"}
                  </td>
                  <td
                    style={{
                      padding: 8,
                      borderBottom: "1px solid #f3f4f6",
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    <button
                      onClick={() => handleToggleActive(u)}
                      disabled={busyId === u.id}
                      style={{
                        background: active ? "#f59e0b" : "#16a34a",
                        color: "white",
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 10px",
                        cursor: "pointer",
                      }}
                    >
                      {busyId === u.id ? "…" : active ? "Désactiver" : "Activer"}
                    </button>
                    <button
                      onClick={() => askDelete(u)}
                      disabled={busyId === u.id || isSelf}
                      style={{
                        background: "#dc2626",
                        color: "white",
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 10px",
                        cursor: isSelf ? "not-allowed" : "pointer",
                        opacity: isSelf ? 0.6 : 1,
                      }}
                    >
                      {busyId === u.id ? "…" : "Supprimer"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 12, color: "#6b7280" }}>
                  Aucun utilisateur pour le moment.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Registre */}
      <h3 style={{ marginTop: 24 }}>Registre des activités (500 dernières)</h3>
      <div
        style={{
          overflowX: "auto",
          maxHeight: 420,
          border: "1px solid #eef2f7",
          borderRadius: 8,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                  width: 180,
                }}
              >
                Date/Heure
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                  width: 220,
                }}
              >
                Événement
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Utilisateur
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 8,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Détails
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((l) => {
              const who =
                l.firstName || l.lastName
                  ? `${l.firstName ?? ""} ${l.lastName ?? ""}`.trim()
                  : l.email ?? "";
              return (
                <tr key={l.id}>
                  <td
                    style={{
                      padding: 8,
                      borderBottom: "1px solid #f3f4f6",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {new Date(l.timestamp).toLocaleString()}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {l.type === "login_success"
                      ? "✅ Connexion"
                      : l.type === "login_failure"
                      ? "❌ Connexion refusée"
                      : l.type === "logout"
                      ? "🔒 Déconnexion"
                      : l.type === "alert_collegue"
                      ? "🟡 Alerte collègue"
                      : l.type === "alert_police"
                      ? "🔵 Alerte police"
                      : l.type === "password_change"
                      ? "🔑 Changement de mot de passe"
                      : l.type}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {who || "—"} {l.userRole ? `(${l.userRole})` : ""}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                    {l.agentName
                      ? `Nom affiché: ${l.agentName}`
                      : l.details || "—"}
                  </td>
                </tr>
              );
            })}
            {filteredLogs.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 12, color: "#6b7280" }}>
                  Aucune entrée à afficher.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmer la suppression"
        message={
          toDelete
            ? `Supprimer l’accès de ${toDelete.firstName} ${toDelete.lastName} (${toDelete.email}) ?`
            : ""
        }
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setToDelete(null);
        }}
      />
    </div>
  );
}

/* ===================== App principale ===================== */
export default function App() {
  const role = useRole();
  const [view, setView] = useState<"main" | "admin">("main");
  const [pwdOpen, setPwdOpen] = useState(false);

  // Audio + interaction
  const alertAudioRef = useRef<HTMLAudioElement | null>(null);
  const hasInteractedRef = useRef<boolean>(false);

  // Alerte "émise par moi"
  const lastAlertIdRef = useRef<string>("");

  // Dédoublonnage & seuil anti-replay
  const seenAlertsRef = useRef<Set<string>>(new Set());
  const thresholdRef = useRef<number>(0);
  if (thresholdRef.current === 0) {
    const saved = Number(localStorage.getItem(LAST_SEEN_ALERT_TS) || "");
    const threshold = Number.isFinite(saved) && saved > 0 ? saved : Date.now();
    localStorage.setItem(LAST_SEEN_ALERT_TS, String(threshold));
    thresholdRef.current = threshold;
  }

  // Nom affiché
  const [agentName, setAgentName] = useState<string>(() => {
    const first = sessionStorage.getItem(USER_FIRST_KEY) || "";
    const last = sessionStorage.getItem(USER_LAST_KEY) || "";
    const fromAccount = `${first} ${last}`.trim();
    if (fromAccount) return fromAccount;
    return localStorage.getItem("cpas_agent_name") || "";
  });

  const isNameFromAccount = Boolean(
    typeof sessionStorage !== "undefined" &&
      (sessionStorage.getItem(USER_FIRST_KEY) ||
        sessionStorage.getItem(USER_LAST_KEY))
  );

  // Info affichée sous les boutons
  const [alertInfo, setAlertInfo] = useState<AlertPayload | null>(null);

  // Notifications : demande au premier chargement si besoin
  useEffect(() => {
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Débloquer l'audio au premier clic
  useEffect(() => {
    const audio = new Audio("/alert.mp3");
    audio.preload = "auto";
    alertAudioRef.current = audio;

    const unlock = () => {
      hasInteractedRef.current = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          console.log("[audio] déverrouillé ✅");
        })
        .catch((err) => {
          console.warn("[audio] déverrouillage refusé :", err);
        });
      window.removeEventListener("click", unlock);
    };

    window.addEventListener("click", unlock);
    return () => window.removeEventListener("click", unlock);
  }, []);

  // Envoi d’alerte
  const sendAlert = async (type: AlertType) => {
    const name = (agentName || "").trim();
    if (!name) {
      alert("Indiquez votre nom avant d’envoyer une alerte.");
      return;
    }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    lastAlertIdRef.current = id;

    const payload: AlertPayload = {
      id,
      agent: name,
      time: new Date().toLocaleTimeString(),
      timestamp: Date.now(),
      type,
    };

    console.log("[alertes] j’envoie sur /alerts :", payload);
    await push(ref(db, "alerts"), payload);

    const email = sessionStorage.getItem(USER_EMAIL_KEY) || undefined;
    const first = sessionStorage.getItem(USER_FIRST_KEY) || undefined;
    const last = sessionStorage.getItem(USER_LAST_KEY) || undefined;
    await logSafely({
      type: type === "collegue" ? "alert_collegue" : "alert_police",
      email,
      firstName: first,
      lastName: last,
      agentName: name,
    });
  };

  // Raccourcis clavier
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || !e.shiftKey) return;
      if (e.key.toLowerCase() === "a") {
        e.preventDefault();
        void sendAlert("collegue");
      }
      if (e.key.toLowerCase() === "p") {
        e.preventDefault();
        void sendAlert("police");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Écoute Firebase des alertes (dédupliquée + anti-replay) */
  useEffect(() => {
    console.log("[alertes] abonnement à /alerts");
    const alertsRef = ref(db, "alerts");
    const seen = seenAlertsRef.current;
    const threshold = thresholdRef.current;

    const unsub = onChildAdded(alertsRef, (snap) => {
      const data = snap.val() as Partial<AlertPayload> | null;
      if (!data) return;

      const ts = Number(data.timestamp || 0);
      if (!ts || ts < threshold) {
        return; // ignore l'historique
      }

      const alertId = data.id || snap.key || "";
      if (!alertId) return;
      if (seen.has(alertId)) return;
      seen.add(alertId);

      const isOwn = alertId === lastAlertIdRef.current;

      const ap: AlertPayload = {
        id: alertId,
        agent: data.agent || "—",
        time: data.time || new Date(ts).toLocaleTimeString(),
        timestamp: ts,
        type: data.type === "police" ? "police" : "collegue",
      };

      // Affiche bandeau sous les boutons
      setAlertInfo(ap);

      // Son/flash/notif si ce n’est pas l’émetteur
      if (!isOwn) {
        const audio = alertAudioRef.current;
        if (audio && hasInteractedRef.current) {
          try {
            audio.currentTime = 0;
            void audio.play();
          } catch (err) {
            console.warn("[audio] lecture refusée :", err);
          }
        }
        const original = document.body.style.backgroundColor;
        const color = ap.type === "police" ? "#1f2f58" : "#fdc02a";
        document.body.style.backgroundColor = color;
        setTimeout(() => {
          document.body.style.backgroundColor = original || "#f3f4f6";
        }, 1500);

        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            const n = new Notification(
              ap.type === "police" ? "🔵 Alerte police" : "🟡 Alerte collègue",
              {
                body: `${ap.agent} – ${ap.time}`,
                icon: new URL("/logo.png", window.location.href).href,
                tag: alertId,
                requireInteraction: true,
              }
            );
            setTimeout(() => n.close(), 10000);
          } catch (err) {
            console.warn("[notif] erreur :", err);
          }
        }
      }

      // met à jour le seuil pour les prochains chargements
      localStorage.setItem(LAST_SEEN_ALERT_TS, String(ts));
    });

    return () => {
      console.log("[alertes] désabonnement de /alerts");
      unsub();
    };
  }, []);

  const saveName = () => {
    if (!isNameFromAccount)
      localStorage.setItem("cpas_agent_name", agentName.trim());
  };

  // ——— Vue Admin ———
  if (view === "admin") {
    return (
      <>
        <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
        <div
          style={{
            minHeight: "100vh",
            background: "#f3f4f6",
            padding: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <AdminPage onBack={() => setView("main")} />
          <button
            onClick={() => setPwdOpen(true)}
            title="Changer mon mot de passe"
            style={{
              position: "absolute",
              top: 16,
              right: 168,
              width: 40,
              height: 40,
              borderRadius: 8,
              border: "none",
              background: "#1f2f58",
              color: "white",
              fontSize: 18,
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            🔑
          </button>
        </div>
      </>
    );
  }

  // ——— Vue principale ———
  return (
    <>
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />

      <div
        style={{
          minHeight: "100vh",
          background: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          padding: 12,
        }}
      >
        <HelpMenu />

        {/* ⚙️ Admin uniquement */}
        {role === "admin" && (
          <button
            onClick={() => setView("admin")}
            title="Espace Administrateur"
            style={{
              position: "absolute",
              top: 16,
              right: 116,
              width: 40,
              height: 40,
              borderRadius: 8,
              border: "none",
              background: "#1f2f58",
              color: "white",
              fontSize: 18,
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            ⚙️
          </button>
        )}

        {/* 🔑 Changer mot de passe */}
        <button
          onClick={() => setPwdOpen(true)}
          title="Changer mon mot de passe"
          style={{
            position: "absolute",
            top: 16,
            right: 168,
            width: 40,
            height: 40,
            borderRadius: 8,
            border: "none",
            background: "#1f2f58",
            color: "white",
            fontSize: 18,
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          🔑
        </button>

        <div
          style={{
            width: "min(560px, 95vw)",
            background: "transparent",
            textAlign: "center",
          }}
        >
          <img
            src="/logo.png"
            alt="CPAS"
            style={{
              width: 84,
              height: 84,
              objectFit: "contain",
              margin: "0 auto 8px",
              display: "block",
              opacity: 0.95,
            }}
            onError={(e) =>
              ((e.target as HTMLImageElement).style.display = "none")
            }
          />
          <h1
            style={{
              margin: "8px 0 16px",
              color: "#1f2f58",
              fontWeight: 800,
              fontSize: 28,
            }}
          >
            CPAS Alert
          </h1>

          {/* Champ Nom */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <input
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              onBlur={saveName}
              placeholder="Entrez votre nom"
              readOnly={isNameFromAccount}
              title={isNameFromAccount ? "Nom issu de votre compte" : undefined}
              style={{
                width: 280,
                height: 36,
                borderRadius: 8,
                border: "1px solid #cfd6e4",
                padding: "0 12px",
                fontSize: 15,
                background: isNameFromAccount ? "#f9fafb" : "white",
                color: "#111827",
              }}
            />
          </div>

          {/* Boutons d’alerte */}
          <div style={{ display: "grid", gap: 16 }}>
            <button
              onClick={() => sendAlert("collegue")}
              style={{
                background: "#f3b81a",
                color: "#1f2f58",
                border: "2px solid #d29d15",
                borderRadius: 16,
                padding: "14px 16px",
                fontSize: 18,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
              }}
            >
              👋 J’ai besoin d’un collègue
            </button>
            <button
              onClick={() => sendAlert("police")}
              style={{
                background: "#1f2f58",
                color: "white",
                border: "2px solid #163053",
                borderRadius: 16,
                padding: "14px 16px",
                fontSize: 18,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
              }}
            >
              🚨 J’ai besoin qu’on appelle la police
            </button>
          </div>

          {/* Bandeau rouge SOUS les boutons */}
          {alertInfo && (
            <div
              role="status"
              aria-live="assertive"
              style={{
                marginTop: 16,
                background: "#ffe9e9",
                border: "2px solid #b30000",
                color: "#b30000",
                fontWeight: 800,
                borderRadius: 12,
                padding: "12px 14px",
                textAlign: "center",
                boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
              }}
            >
              {alertInfo.agent} a déclenché une alerte (
              {alertInfo.type === "collegue" ? "Besoin d’un collègue" : "alerte police"}
              ) à {alertInfo.time}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
